/**
 * FRONT-3369: before the owner publishes (the Live switch, Publish changes,
 * an accept's Publish now), one short confirmation built from the server's
 * summary: how many changes by whom, the changed pages, and a preview of each
 * accepted draft. The publish names the version the summary described; when
 * the app moved while the confirmation was open, the server hands back the
 * new summary and the owner is asked again.
 */
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '~/components/ui/accordion';
import { ApiError, api } from '~/lib/api-client';

export interface PublishVersion {
  main: string;
  live: string;
}

export type ChangePart =
  | { kind: 'you'; count: number }
  | { kind: 'draft' | 'person'; userId: string; name: string; count: number }
  | { kind: 'other'; count: number };

export interface PublishSummary {
  version: PublishVersion;
  changes: number | null;
  parts: ChangePart[];
  /** The server's own sentence, so every surface says the same thing. */
  sentence: string;
  /** One line per contributor, only when the sentence names just the first few. */
  partLines: string[];
  pages: string[];
  morePages: number;
  otherFiles: number;
  previews: Array<{ userId: string; name: string | null; isYou: boolean }>;
}

export const MOVED_NOTE = 'The app changed while you were looking. This is what publishing includes now.';
export const KEPT_CHANGING = 'This app kept changing while you looked at what publishing includes. Try again in a moment.';
/** How many times the owner is asked again before giving up on an app that keeps moving. */
const ASK_ROUNDS = 3;

/** Null for an app without project history: it publishes as before, with nothing to summarise. */
export async function fetchPublishSummary(slug: string): Promise<PublishSummary | null> {
  return (await api.get<{ summary: PublishSummary | null }>(`/api/admin/apps/${slug}/publish-summary`)).summary;
}

/** The fresh summary a publish refused for a moved app carries; undefined for any other error. */
export function movedSummary(err: unknown): PublishSummary | null | undefined {
  if (!(err instanceof ApiError) || err.status !== 409) return undefined;
  const body = err.body as { code?: unknown; summary?: unknown } | undefined;
  if (body?.code !== 'summary_changed') return undefined;
  return (body.summary as PublishSummary | null | undefined) ?? null;
}

/**
 * Reads the summary, asks the owner, and publishes the version they saw. Null
 * when they chose not to. A summary that cannot be read is thrown, so nothing
 * is published that was not described.
 */
export async function publishWithSummary<T>(deps: {
  summary: () => Promise<PublishSummary | null>;
  ask: (summary: PublishSummary, moved: boolean) => Promise<boolean>;
  publish: (expected: PublishVersion | undefined) => Promise<T | null>;
}): Promise<T | null> {
  let summary = await deps.summary();
  if (!summary) return deps.publish(undefined);
  let moved = false;
  for (let round = 0; round < ASK_ROUNDS; round++) {
    if (!(await deps.ask(summary, moved))) return null;
    try {
      return await deps.publish(summary.version);
    } catch (err) {
      const fresh = movedSummary(err);
      if (fresh === undefined || fresh === null) throw err;
      summary = fresh;
      moved = true;
    }
  }
  throw new Error(KEPT_CHANGING);
}

/** The pages line: "Home page, about and team", with the rest counted in words. */
export function pagesLine(s: Pick<PublishSummary, 'pages' | 'morePages' | 'otherFiles'>): string | null {
  const names = s.morePages ? [...s.pages, `${s.morePages} more ${s.morePages === 1 ? 'page' : 'pages'}`] : s.pages;
  const files = s.otherFiles === 1 ? '1 other file' : `${s.otherFiles} other files`;
  if (!names.length) return s.otherFiles ? `No pages change, only ${files}.` : null;
  const listed = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${names.length === 1 && !s.morePages ? 'Page that changes' : 'Pages that change'}: ${listed}${s.otherFiles ? `, plus ${files}` : ''}.`;
}

/** How many draft previews the confirmation links; the rest are in the Edit page's Drafts section. */
const PREVIEWS_SHOWN = 3;

/** The line under the capped preview links; null when every preview is linked. */
export function morePreviewsLine(total: number): string | null {
  const more = total - PREVIEWS_SHOWN;
  if (more <= 0) return null;
  return more === 1 ? '1 more draft is in the Drafts section.' : `${more} more drafts are in the Drafts section.`;
}

/** The address of a draft's own preview tab (FRONT-3367). */
export function draftPreviewHref(slug: string, userId: string): string {
  return `/admin/apps/${slug}/drafts/${encodeURIComponent(userId)}/preview`;
}

export function PublishSummaryBody({ slug, summary, moved, previewOn }: { slug: string; summary: PublishSummary; moved: boolean; previewOn: boolean }) {
  const pages = pagesLine(summary);
  const previews = previewOn ? summary.previews : [];
  const morePreviews = morePreviewsLine(previews.length);
  return (
    <div data-testid="publish-summary" className="space-y-2 text-sm text-muted-foreground">
      {moved && (
        <p data-testid="publish-summary-moved" className="text-foreground">
          {MOVED_NOTE}
        </p>
      )}
      <p data-testid="publish-summary-sentence" className="text-foreground">
        {summary.sentence}
      </p>
      {summary.partLines.length > 0 && (
        <Accordion type="single" collapsible>
          <AccordionItem value="who" className="border-b-0" data-testid="publish-summary-who">
            <AccordionTrigger
              data-testid="publish-summary-who-toggle"
              className="w-fit flex-none gap-1 py-0 text-xs font-normal text-muted-foreground hover:text-foreground hover:no-underline">
              Who's in this publish
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                {summary.partLines.map((line) => (
                  <li key={line} data-testid="publish-summary-who-line">
                    {line}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {pages && <p data-testid="publish-summary-pages">{pages}</p>}
      {previews.length > 0 && (
        <ul className="space-y-1">
          {previews.slice(0, PREVIEWS_SHOWN).map((p) => (
            <li key={p.userId}>
              <a
                href={draftPreviewHref(slug, p.userId)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="publish-summary-preview"
                className="text-primary underline-offset-2 hover:underline"
              >
                {p.isYou ? 'Preview your draft' : `Preview ${p.name ?? 'a colleague'}'s draft`}
              </a>
            </li>
          ))}
        </ul>
      )}
      {morePreviews && <p data-testid="publish-summary-more-previews">{morePreviews}</p>}
    </div>
  );
}

/** The confirmation's words: going live from offline, or publishing a live app's waiting changes. */
export function summaryDialog(goingLive: boolean) {
  return goingLive
    ? { title: 'Make this app live?', confirmText: 'Go live', cancelText: 'Not now' }
    : { title: 'Publish these changes?', confirmText: 'Publish', cancelText: 'Not now' };
}
